package me.repeater64.advancedmpkeditor.gui.screens

import androidx.compose.foundation.ExperimentalFoundationApi
import androidx.compose.foundation.PointerMatcher
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxScope
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.onClick
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.DropdownMenu
import androidx.compose.material3.DropdownMenuItem
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.ExperimentalMaterial3ExpressiveApi
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.key
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.produceState
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.runtime.snapshotFlow
import androidx.compose.runtime.toMutableStateList
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.input.pointer.PointerButton
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import me.repeater64.advancedmpkeditor.backend.commands.CircularConditionsException
import me.repeater64.advancedmpkeditor.backend.data_object.CopyPasteable
import me.repeater64.advancedmpkeditor.backend.data_object.fixed_slot.InventorySlotData
import me.repeater64.advancedmpkeditor.backend.data_object.item.DontReplaceMinecraftItem
import me.repeater64.advancedmpkeditor.backend.data_object.item.ForcedEmptyMinecraftItem
import me.repeater64.advancedmpkeditor.backend.data_object.item.MinecraftItem
import me.repeater64.advancedmpkeditor.backend.data_object.item.SimpleMinecraftItem
import me.repeater64.advancedmpkeditor.backend.data_object.random_slot.RandomSlotOptionsSet
import me.repeater64.advancedmpkeditor.backend.data_object.randomiser.WeightedOption
import me.repeater64.advancedmpkeditor.backend.data_object.randomiser.WeightedOptionList
import me.repeater64.advancedmpkeditor.backend.data_object.saved_hotbar.AirItem
import me.repeater64.advancedmpkeditor.backend.data_object.saved_hotbar.BarrelItem
import me.repeater64.advancedmpkeditor.backend.data_object.saved_hotbar.CommandBlockItem
import me.repeater64.advancedmpkeditor.backend.data_object.saved_hotbar.SavedHotbarItem
import me.repeater64.advancedmpkeditor.backend.data_object.saved_hotbar.SavedHotbars
import me.repeater64.advancedmpkeditor.backend.presets_examples.presets.BarrelPreset
import me.repeater64.advancedmpkeditor.backend.presets_examples.BlankBarrel
import me.repeater64.advancedmpkeditor.gui.component.DragDropContainer
import me.repeater64.advancedmpkeditor.gui.component.DragSwappable
import me.repeater64.advancedmpkeditor.gui.component.MinecraftItemIcon
import me.repeater64.advancedmpkeditor.gui.component.MinecraftSlotDisplay
import me.repeater64.advancedmpkeditor.gui.component.SimpleDropdown
import me.repeater64.advancedmpkeditor.gui.component.dragAutoScroll
import me.repeater64.advancedmpkeditor.gui.component.verticalColumnScrollbar
import me.repeater64.advancedmpkeditor.gui.platform.HotbarNbtFileManager
import me.repeater64.advancedmpkeditor.gui.platform.PreventAppExit
import me.repeater64.advancedmpkeditor.gui.platform.trackEvent
import me.repeater64.advancedmpkeditor.gui.screens.barrel.BarrelEditor

@OptIn(ExperimentalMaterial3Api::class, ExperimentalMaterial3ExpressiveApi::class, ExperimentalFoundationApi::class) // Needed for ExposedDropdownMenuBox
@Composable
fun EditorScreen(
    hotbars: SavedHotbars,
    fileManager: HotbarNbtFileManager,

    pendingNavigationRequest: Screen?,
    onConfirmNavigation: (Screen) -> Unit,
    onCancelNavigation: () -> Unit
) {

    var savedHotbarsHash by remember { mutableStateOf(hotbars.contentHash()) }
    val currentHotbarsHash by produceState(initialValue = savedHotbarsHash, key1 = hotbars) {
        snapshotFlow { hotbars.contentHash() }
            .collect { value = it }
    }
    val isSaved = (currentHotbarsHash == savedHotbarsHash )

    var isSaving by remember { mutableStateOf(false) }
    var showUnsavedDialog by remember { mutableStateOf(false) }

    var showGeneralWarningDialog by remember { mutableStateOf(false) }
    var generalWarningDialogText by remember { mutableStateOf("nothing yet") }
    var generalWarningProceedAction by remember { mutableStateOf<() -> Any>({}) }
    var generalWarningIsError by remember { mutableStateOf(false) }

    var showEditorDialog by remember { mutableStateOf(false) }
    var editorDialog by remember { mutableStateOf<@Composable () -> Any>( {} ) }

    var selectedHotbarIndex by remember { mutableStateOf(0) }

    var currentlyEditingItemIndex by remember { mutableStateOf(-1) }
    fun currentlyEditingItem(): SavedHotbarItem = if (currentlyEditingItemIndex >= 0) hotbars.hotbars[selectedHotbarIndex].hotbarItems[currentlyEditingItemIndex] else AirItem()

    var clipboard by remember { mutableStateOf<CopyPasteable<*>?>(null)}

    fun getClipboard(): CopyPasteable<*>? = clipboard
    fun setClipboard(it: CopyPasteable<*>?) { clipboard = it }



    PreventAppExit(!isSaved)

    LaunchedEffect(pendingNavigationRequest) {
        if (pendingNavigationRequest != null) {
            if (isSaved) {
                // Safe to go, approve immediately
                onConfirmNavigation(pendingNavigationRequest)
            } else {
                // Not saved, trigger the local dialog
                showUnsavedDialog = true
            }
        }
    }

    fun showGeneralWarning(dialogText: String, proceedAction: () -> Any, isError: Boolean = false) {
        generalWarningDialogText = dialogText
        generalWarningProceedAction = proceedAction
        generalWarningIsError = isError
        showGeneralWarningDialog = true
    }

    @Composable
    fun saveButton() {
        Button(
            onClick = {
                try {
                    isSaving = true
                    fileManager.saveHotbars(hotbars, "hotbar.nbt")
                    isSaving = false
                    savedHotbarsHash = currentHotbarsHash
                    trackEvent("download", "Download")
                } catch (e: CircularConditionsException) {
                    isSaving = false
                    showGeneralWarning("Circular randomiser link dependency! You've set up randomiser links that are impossible to resolve due to a circular dependency, in barrel: ${e.barrelName}. This is caused by a situation like: One list has an option that triggers A, and an option that depends on B, and another list has an option that triggers B and an option that depends on A. The relevant condition labels in your circular dependency are: ${e.problematicLabels}",
                        {}, true)
                } catch (e: Exception) {
                    isSaving = false
                    trackEvent("error_download", "Failed Download")
                    showGeneralWarning("An unexpected error occured whilst trying to download hotbar.nbt! This is a bug of some kind, please copy/paste the below error message and send it to \"repeater64\" on discord (or create a github issue).\n\n: ${e.stackTraceToString()}",
                        {}, true)
                }
            },
            colors = ButtonDefaults.buttonColors(containerColor = MaterialTheme.colorScheme.primary),
            modifier = Modifier
        ) {
            Text(if (isSaving) "Saving..." else "Download / Save All")
        }
    }

    @Composable
    fun topPanel() {
        // Top panel with hotbar selector and saved/unsaved text
        @Composable
        fun BoxScope.unsavedChangesText() {
            Row(modifier = Modifier.align(Alignment.CenterStart)) {
                Spacer(Modifier.width(50.dp))
                Text(
                    if (isSaved) "All Changes Saved" else "Unsaved Changes",
                    style = MaterialTheme.typography.titleMedium,
                    fontWeight = FontWeight.Bold,
                    color = if (isSaved) MaterialTheme.colorScheme.secondary else MaterialTheme.colorScheme.error
                )
            }
        }

        Spacer(Modifier.height(5.dp))
        Box(
            modifier = Modifier.fillMaxWidth()
        ) {

            // Unsaved changes/all changes saved text
            unsavedChangesText()

            // Hotbar selector dropdown
            key(selectedHotbarIndex) {SimpleDropdown(
                modifier = Modifier.width(300.dp).align(Alignment.Center),
                label = "Select Hotbar",
                selectedValue = "Hotbar ${selectedHotbarIndex + 1}",
                options = hotbars.hotbars.indices.map { "Hotbar ${it + 1}" },
                optionSelected = { selectedHotbarIndex = it; currentlyEditingItemIndex = -1 },
            )}

            // Save all button
            Row(modifier = Modifier.align(Alignment.CenterEnd)) { saveButton(); Spacer(Modifier.width(50.dp)) }
        }
        Spacer(Modifier.height(12.dp))
    }

    Column(modifier = Modifier.fillMaxSize(), horizontalAlignment = Alignment.CenterHorizontally) {

        topPanel()

        // DragDropContainer for fixed + random slots, indexed by 0-8 is hotbar, 9+ is inv, 36 is offhand, 37+ is random slot itemsets, -1 is the "add new random options set" button
        DragDropContainer(contentAlignment = Alignment.Center,
            onSwap = { srcKey, destKey ->
                val currentlyEditingItem = currentlyEditingItem()
                if (currentlyEditingItem !is BarrelItem) return@DragDropContainer
                val fixedSlotsData = currentlyEditingItem.fixedSlotsData
                val randomSlotsData = currentlyEditingItem.randomSlotsData

                fun isFixedSlotKey(key: Int) = key != -1 && key <= 36

                if (isFixedSlotKey(srcKey) && isFixedSlotKey(destKey)) {
                    // This is a swap between two fixed slots

                    val srcSlot = if (srcKey == 36) fixedSlotsData.offhandSlotData else if (srcKey < 9) fixedSlotsData.hotbarSlotsData[srcKey] else fixedSlotsData.inventorySlotsData[srcKey - 9]
                    val destSlot = if (destKey == 36) fixedSlotsData.offhandSlotData else if (destKey < 9) fixedSlotsData.hotbarSlotsData[destKey] else fixedSlotsData.inventorySlotsData[destKey - 9]

                    var srcItems = srcSlot.itemOptions
                    var destItems = destSlot.itemOptions

                    if (srcSlot is InventorySlotData && destSlot !is InventorySlotData && srcItems.options.size == 1 && srcItems.options[0].option is DontReplaceMinecraftItem) {
                        // Moving an "available for random items" slot out of the inventory, convert it to forced empty slot
                        srcItems = WeightedOptionList(mutableListOf(WeightedOption(ForcedEmptyMinecraftItem(), 1)))
                    } else if (destSlot is InventorySlotData && srcSlot !is InventorySlotData && destItems.options.size == 1 && destItems.options[0].option is DontReplaceMinecraftItem) {
                        // Same story
                        destItems = WeightedOptionList(mutableListOf(WeightedOption(ForcedEmptyMinecraftItem(), 1)))
                    }

                    srcSlot.itemOptions = destItems
                    destSlot.itemOptions = srcItems
                } else if (!isFixedSlotKey(srcKey) && !isFixedSlotKey(destKey)) {
                    // Swap between two random slots
                    val srcIndex = srcKey - 37
                    val destIndex = destKey - 37

                    val srcOptionset = randomSlotsData.optionsSets[srcIndex]

                    if (destIndex < 0) {
                        // We're trying to drag it to the bottom of the list
                        randomSlotsData.optionsSets.removeAt(srcIndex)
                        randomSlotsData.optionsSets.add(srcOptionset)
                    } else {
                        // It's a normal swap
                        randomSlotsData.optionsSets[srcIndex] = randomSlotsData.optionsSets[destIndex]
                        randomSlotsData.optionsSets[destIndex] = srcOptionset
                    } // srcIndex being negative shouldn't be possible as that slot is marked as always empty, so dragging from it is not allowed
                } else {
                    // Swap between fixed and random slot
                    val fixedSlotKey = if (isFixedSlotKey(srcKey)) srcKey else destKey
                    val randomIndex = if (isFixedSlotKey(srcKey)) destKey-37 else srcKey-37

                    var randomOptionsSet = if (randomIndex >= 0) randomSlotsData.optionsSets[randomIndex] else null
                    val fixedSlot = if (fixedSlotKey == 36) fixedSlotsData.offhandSlotData else if (fixedSlotKey < 9) fixedSlotsData.hotbarSlotsData[fixedSlotKey] else fixedSlotsData.inventorySlotsData[fixedSlotKey - 9]

                    // Check the random options set doesn't have more than one stack for any option
                    if (randomOptionsSet != null && !randomOptionsSet.canAlwaysFitInOneSlot()) {
                        if (isFixedSlotKey(srcKey)) {
                            // We dragged from a fixed slot onto an invalid random slot. Interpret this as wanting to just insert the fixed slot data into the random slot list, but not swap
                            randomOptionsSet = null
                        } else {
                            showGeneralWarning("Can't perform this swap because the random slot itemset \"${randomOptionsSet.setName}\" has options that take up more than one stack, so they can't fit in a single slot!", {}, true)
                            return@DragDropContainer
                        }
                    }

                    // Build the random slot options set from the fixed slots data, mapping ForcedEmptyMinecraftItem into dont replace
                    val newRandomSlotOptionsSet = RandomSlotOptionsSet("TODO", WeightedOptionList(fixedSlot.itemOptions.options.map { weightedOption -> WeightedOption(listOf(weightedOption.option.let { if (it is ForcedEmptyMinecraftItem) DontReplaceMinecraftItem() else it }).toMutableStateList()) }.toMutableList()))
                    newRandomSlotOptionsSet.nameSelf()

                    // Build fixed slot item list from random slots data, mapping DontReplaceMinecraftItems into forced empty
                    val newFixedItems = if (randomOptionsSet == null) {
                        if (fixedSlot is InventorySlotData) {
                            WeightedOptionList(mutableListOf(WeightedOption(DontReplaceMinecraftItem() as MinecraftItem)))
                        } else {
                            WeightedOptionList(mutableListOf(WeightedOption(ForcedEmptyMinecraftItem() as MinecraftItem)))
                        }
                    } else {
                        WeightedOptionList(randomOptionsSet.options.options.map { weightedOption -> WeightedOption(weightedOption.option.first().let { if (it is DontReplaceMinecraftItem) ForcedEmptyMinecraftItem() else it }, weightedOption.weight, weightedOption.label, weightedOption.conditions) }.toMutableList())
                    }

                    // Check if the fixed slot was just empty
                    if (fixedSlot.itemOptions.options.size == 1 && (fixedSlot.itemOptions.options[0].option is DontReplaceMinecraftItem || fixedSlot.itemOptions.options[0].option is ForcedEmptyMinecraftItem)) {
                        // If so, just remove the random options set, unless the random set was also nothing then do nothing
                        if (randomOptionsSet == null) return@DragDropContainer
                        randomSlotsData.optionsSets.removeAt(randomIndex)
                    } else {
                        // Otherwise, proper swap
                        if (randomIndex >= 0 && randomOptionsSet != null) {
                            randomSlotsData.optionsSets[randomIndex] = newRandomSlotOptionsSet
                        } else if (randomIndex >= 0) {
                            // (Or insert)
                            randomSlotsData.optionsSets.add(randomIndex+1, newRandomSlotOptionsSet)
                        } else {
                            // (or in this case add to the end)
                            randomSlotsData.optionsSets.add(newRandomSlotOptionsSet)
                        }
                    }

                    fixedSlot.itemOptions = newFixedItems
                }

            },
            emptyChecker = { key ->
                if (key == -1) return@DragDropContainer true
                val currentlyEditingItem = currentlyEditingItem()
                if (currentlyEditingItem !is BarrelItem) return@DragDropContainer false
                if (key <= 36) {
                    val fixedSlotsData = currentlyEditingItem.fixedSlotsData
                    val slot =
                        if (key == 36) fixedSlotsData.offhandSlotData else if (key < 9) fixedSlotsData.hotbarSlotsData[key] else fixedSlotsData.inventorySlotsData[key - 9]

                    (slot is InventorySlotData && slot.itemOptions.options.size == 1 && slot.itemOptions.options[0].option is DontReplaceMinecraftItem)
                } else {
                    false
                }
            }
        ) {
            val scrollState = rememberScrollState()
            Column(
                modifier = Modifier
                    .fillMaxSize()
                    .verticalColumnScrollbar(scrollState).verticalScroll(scrollState).dragAutoScroll(scrollState),
                horizontalAlignment = Alignment.CenterHorizontally
            ) {

                // Hotbar items selector
                DragDropContainer(modifier = Modifier.fillMaxWidth(), contentAlignment = Alignment.Center,
                    onSwap = { srcKey, destKey ->
                        val hotbar = hotbars.hotbars[selectedHotbarIndex]
                        val srcData = hotbar.hotbarItems[srcKey]
                        hotbar.hotbarItems[srcKey] = hotbar.hotbarItems[destKey]
                        hotbar.hotbarItems[destKey] = srcData

                        if (currentlyEditingItemIndex == srcKey && srcData is BarrelItem) {
                            // We moved the currently editing barrel to destKey. Follow it
                            currentlyEditingItemIndex = destKey
                        } else if (currentlyEditingItemIndex == destKey && hotbar.hotbarItems[srcKey] is BarrelItem) {
                            // We moved something else into the currently editing slot, so the barrel we were editing has moved to srcKey. Follow it
                            currentlyEditingItemIndex = srcKey
                        }
                    },
                    emptyChecker = {hotbars.hotbars[selectedHotbarIndex].hotbarItems[it] is AirItem}
                ) {
                    Row {
                        val selectedHotbarItems = hotbars.hotbars[selectedHotbarIndex].hotbarItems
                        for ((i, savedHotbarItem) in selectedHotbarItems.withIndex()) {
                            var dropdownExpanded by remember {mutableStateOf(false)}

                            val onLeftClick = {
                                if (savedHotbarItem is AirItem) { // Open dropdown with add barrel/add cmd block options
                                    dropdownExpanded = true
                                } else if (savedHotbarItem is BarrelItem) {
                                    currentlyEditingItemIndex = i
                                }
                            }
                            val onRightClick = {
                                if (savedHotbarItem !is AirItem) {
                                    val deleteAction = {
                                        selectedHotbarItems[i] = AirItem()

                                        if (currentlyEditingItemIndex == i) {
                                            currentlyEditingItemIndex = -1
                                        }
                                    }
                                    if (savedHotbarItem is BarrelItem) {
                                        showGeneralWarning("Are you sure you want to delete the entire configuration barrel \"${savedHotbarItem.name}\"? This can't be undone!", deleteAction)
                                    } else {
                                        deleteAction()
                                    }
                                }
                            }
                            val onMiddleClick = {
                                if (savedHotbarItem is BarrelItem) {
                                    // Look for blank slot to put it
                                    var targetIndex = -1
                                    for ((index, item) in selectedHotbarItems.withIndex()) {
                                        if (item is AirItem) {
                                            targetIndex = index
                                            break
                                        }
                                    }

                                    if (targetIndex == -1) {
                                        // Couldn't find - pop up error
                                        showGeneralWarning("There isn't an empty slot in this hotbar to duplicate this barrel into! Please clear out a slot.", {}, true)
                                    } else {
                                        selectedHotbarItems[targetIndex] = savedHotbarItem.deepCopy()
                                    }
                                }
                            }

                            val slotDisplay = MinecraftSlotDisplay(savedHotbarItem.getGuiRepresentationItem(), 80,
                                tooltipContents = {
                                    Column {
                                        Text(savedHotbarItem.getGuiName(), style = MaterialTheme.typography.titleSmallEmphasized, fontWeight = FontWeight.Bold)
                                        Spacer(Modifier.height(5.dp))
                                        when (savedHotbarItem) {
                                            is AirItem -> Text("Click to add item", style = MaterialTheme.typography.bodyMedium)
                                            is BarrelItem -> Text("Left Click to edit, Right Click to delete, Middle Click to duplicate", style = MaterialTheme.typography.bodyMedium)
                                            is CommandBlockItem -> Text("Right Click to delete", style = MaterialTheme.typography.bodyMedium)
                                        }
                                        if (savedHotbarItem !is AirItem) {
                                            Spacer(Modifier.height(5.dp))
                                            Text("Click and drag to swap hotbar slots", style = MaterialTheme.typography.bodyMedium)
                                        }
                                    }
                                },
                                highlighted = currentlyEditingItemIndex == i,
                                modifier = Modifier.onClick(matcher = PointerMatcher.mouse(PointerButton.Primary), onClick = onLeftClick)
                                    .onClick(matcher = PointerMatcher.mouse(PointerButton.Secondary), onClick = onRightClick)
                                    .onClick(matcher = PointerMatcher.mouse(PointerButton.Tertiary), onClick = onMiddleClick)
                            )

                            Spacer(Modifier.width(2.dp))
                            Box(contentAlignment = Alignment.Center) {
                                DragSwappable(
                                    key = i,
                                    ghostContent = { slotDisplay.ContentsOnly() },
                                    content = { isDragging, isHovered -> slotDisplay.SlotDisplay(!isDragging, isHovered) }
                                )
                                DropdownMenu(expanded = dropdownExpanded, onDismissRequest = { dropdownExpanded = false }) {
                                    var subDropdownOpen by remember {mutableStateOf(false)}
                                    DropdownMenuItem(
                                        text = { Text("Add Blank Configuration Barrel") },
                                        leadingIcon = { MinecraftItemIcon(SimpleMinecraftItem("barrel", 1), modifier=Modifier.size(50.dp)) },
                                        onClick = {
                                            // Add barrel to i slot
                                            selectedHotbarItems[i] = BlankBarrel.barrel
                                            dropdownExpanded = false
                                            currentlyEditingItemIndex = i
                                        }
                                    )
                                    Spacer(Modifier.height(8.dp))
                                    DropdownMenuItem(
                                        text = { Text("Add Configuration Barrel from Preset") },
                                        leadingIcon = { MinecraftItemIcon(SimpleMinecraftItem("barrel", 1), modifier=Modifier.size(50.dp)) },
                                        onClick = {
                                            // Open sub dropdown with presets
                                            subDropdownOpen = true
                                        },
                                        trailingIcon = {
                                            DropdownMenu(subDropdownOpen, onDismissRequest = { subDropdownOpen = false}) {
                                                for (preset in BarrelPreset.entries) {
                                                    DropdownMenuItem(
                                                        text = { Text(preset.displayName) },
                                                        onClick = {
                                                            // Add barrel to i slot
                                                            selectedHotbarItems[i] = preset.barrelGetter()
                                                            subDropdownOpen = false
                                                            dropdownExpanded = false
                                                            currentlyEditingItemIndex = i
                                                        }
                                                    )
                                                }
                                            }
                                        }
                                    )
                                    Spacer(Modifier.height(8.dp))
                                    DropdownMenuItem(
                                        text = { Text("Add MPK Command Block") },
                                        leadingIcon = { MinecraftItemIcon(SimpleMinecraftItem("repeating_command_block", 1), modifier=Modifier.size(50.dp)) },
                                        onClick = {
                                            // Add cmd block to i slot
                                            selectedHotbarItems[i] = CommandBlockItem()
                                            dropdownExpanded = false
                                        }
                                    )
                                }
                            }
                            Spacer(Modifier.width(2.dp))
                        }
                    }
                }

                Spacer(Modifier.height(12.dp))

                // Barrel editor, or text telling you to select a barrel
                key(currentlyEditingItemIndex) { Column(modifier = Modifier.fillMaxSize(), horizontalAlignment = Alignment.CenterHorizontally) {
                    val currentlyEditingItem = currentlyEditingItem()
                    if (currentlyEditingItem !is BarrelItem) {
                        Spacer(Modifier.height(100.dp))
                        Text(
                            text = "Click on a barrel (or add one) to edit its configuration!",
                            color = MaterialTheme.colorScheme.onSurface,
                            style = MaterialTheme.typography.titleLarge
                        )
                    }
                    else {
                        BarrelEditor(
                            barrelItem = currentlyEditingItem,
                            showDialogCallback = { dialog ->
                                showEditorDialog = true
                                editorDialog = dialog
                            },
                            hideDialogCallback = {showEditorDialog = false},
                            getClipboardCallback = { getClipboard() },
                            setClipboardCallback = { setClipboard(it) },
                        )
                    }
                }}

                Spacer(Modifier.weight(1f)) // Push save button to bottom
                Spacer(Modifier.height(24.dp)) // A bit of space even if no spare room to push save button all the way to bottom


                Row {
                    Spacer(Modifier.weight(1f))
                    saveButton()
                    Spacer(Modifier.width(50.dp))
                }

                Spacer(Modifier.height(24.dp))
            }

            if (showUnsavedDialog) {
                UnsavedChangesDialog(
                    onConfirmExit = {
                        showUnsavedDialog = false
                        onConfirmNavigation(pendingNavigationRequest!!)
                    },
                    onDismiss = {
                        showUnsavedDialog = false
                        onCancelNavigation() // Tell parent to clear the request
                    }
                )
            } else if (showGeneralWarningDialog) {
                GeneralWarningDialog(
                    warningText = generalWarningDialogText,
                    onProceed = {
                        showGeneralWarningDialog = false
                        generalWarningProceedAction()
                    },
                    onDismiss = {
                        showGeneralWarningDialog = false
                    },
                    isError = generalWarningIsError
                )
            } else if (showEditorDialog) {
                editorDialog()
            }
        }
    }
}

@Composable
fun UnsavedChangesDialog(
    onConfirmExit: () -> Unit,
    onDismiss: () -> Unit
) {
    AlertDialog(
        onDismissRequest = onDismiss,
        title = { Text("Unsaved Changes") },
        text = { Text("You have unsaved changes. Are you sure you want to exit?") },
        confirmButton = {
            Button(
                onClick = onConfirmExit,
                colors = ButtonDefaults.buttonColors(containerColor = MaterialTheme.colorScheme.error)
            ) {
                Text("Exit (Discard Changes)")
            }
        },
        dismissButton = {
            TextButton(onClick = onDismiss) {
                Text("Cancel")
            }
        }
    )
}

@Composable
fun GeneralWarningDialog(
    warningText: String,
    onProceed: () -> Unit,
    onDismiss: () -> Unit,
    isError: Boolean
) {
    AlertDialog(
        onDismissRequest = onDismiss,
        title = { Text(if (isError) "Error" else "Warning") },
        text = {
            val scrollState = rememberScrollState()
            Column(
                modifier = Modifier.padding(10.dp).verticalColumnScrollbar(scrollState).verticalScroll(scrollState),
                horizontalAlignment = Alignment.CenterHorizontally,
            ) {
                Text(warningText)
            }
             },
        confirmButton = {
            Button(
                onClick = onProceed,
                colors = if (isError) ButtonDefaults.buttonColors() else ButtonDefaults.buttonColors(containerColor = MaterialTheme.colorScheme.error)
            ) {
                Text(if (isError) "Ok" else "Proceed")
            }
        },
        dismissButton = if (isError) null else {{
            TextButton(onClick = onDismiss) {
                Text("Cancel")
            }
        }}
    )
}